<template>
  <div class="project-card" @click="$emit('click', project)">
    <div class="project-card__header">
      <div class="project-card__info">
        <h3 class="project-card__name">{{ project.name }}</h3>
        <span v-if="project.client" class="project-card__client">
          {{ project.client.name }}
        </span>
      </div>
      <span
        class="project-card__badge"
        :class="`project-card__badge--${project.status}`"
      >
        {{ statusLabel }}
      </span>
    </div>

    <div class="project-card__metrics">
      <div class="metric">
        <span class="metric__label">Presupuesto</span>
        <span class="metric__value">${{ formatNum(project.budget) }}</span>
      </div>
      <div class="metric">
        <span class="metric__label">Facturado</span>
        <span class="metric__value"
          >${{ formatNum(project.total_invoiced) }}</span
        >
      </div>
      <div class="metric">
        <span class="metric__label">Gastos</span>
        <span class="metric__value metric__value--red"
          >${{ formatNum(project.total_expenses) }}</span
        >
      </div>
      <div class="metric">
        <span class="metric__label">Utilidad</span>
        <span
          class="metric__value"
          :class="
            project.gross_profit >= 0
              ? 'metric__value--green'
              : 'metric__value--red'
          "
        >
          ${{ formatNum(project.gross_profit) }}
        </span>
      </div>
    </div>

    <div v-if="project.budget" class="project-card__progress">
      <div class="progress-bar">
        <div
          class="progress-bar__fill"
          :style="{ width: progressPct + '%' }"
          :class="progressPct >= 100 ? 'progress-bar__fill--full' : ''"
        ></div>
      </div>
      <span class="progress-label"
        >{{ progressPct }}% facturado del presupuesto</span
      >
    </div>

    <div class="project-card__footer">
      <span v-if="project.end_date" class="project-card__date">
        Entrega: {{ formatDate(project.end_date) }}
      </span>
      <span class="project-card__invoices"
        >{{ project.invoice_count }} factura{{
          project.invoice_count !== 1 ? "s" : ""
        }}</span
      >
    </div>
  </div>
</template>

<script>
export default {
  name: "ProjectCard",
  props: {
    project: { type: Object, required: true }
  },
  computed: {
    statusLabel() {
      const labels = {
        active: "Activo",
        paused: "Pausado",
        completed: "Completado",
        cancelled: "Cancelado"
      };
      return labels[this.project.status] || this.project.status;
    },
    progressPct() {
      if (!this.project.budget || this.project.budget === 0) return 0;
      return Math.min(
        100,
        Math.round((this.project.total_invoiced / this.project.budget) * 100)
      );
    }
  },
  methods: {
    formatNum(val) {
      if (!val && val !== 0) return "—";
      return Number(val).toLocaleString("es-EC", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    formatDate(val) {
      if (!val) return "";
      return new Date(val).toLocaleDateString("es-EC", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.project-card {
  background: #fff;
  border: 1.5px solid #eef0f8;
  border-radius: 14px;
  padding: 20px;
  cursor: pointer;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 20px rgba(45, 43, 85, 0.1);
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
  }

  &__name {
    font-size: 15px;
    font-weight: 700;
    color: #2d2b55;
    margin: 0 0 4px;
  }

  &__client {
    font-size: 12px;
    color: #888;
  }

  &__badge {
    font-size: 11px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 20px;
    white-space: nowrap;

    &--active {
      background: #e8faf6;
      color: #1d9e75;
    }
    &--paused {
      background: #fff8e6;
      color: #d08b00;
    }
    &--completed {
      background: #eef2ff;
      color: #534ab7;
    }
    &--cancelled {
      background: #fef2f2;
      color: #ef4444;
    }
  }

  &__metrics {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    margin-bottom: 14px;
  }

  &__progress {
    margin-bottom: 14px;
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: #999;
  }

  &__date,
  &__invoices {
    font-size: 12px;
    color: #aaa;
  }
}

.metric {
  display: flex;
  flex-direction: column;
  gap: 2px;

  &__label {
    font-size: 10px;
    color: #aaa;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__value {
    font-size: 13px;
    font-weight: 700;
    color: #2d2b55;

    &--green {
      color: #1d9e75;
    }
    &--red {
      color: #ef4444;
    }
  }
}

.progress-bar {
  height: 6px;
  background: #f0f0f8;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 4px;

  &__fill {
    height: 100%;
    background: linear-gradient(90deg, #3ecfb4, #1a3a5c);
    border-radius: 4px;
    transition: width 0.4s;

    &--full {
      background: #1d9e75;
    }
  }
}

.progress-label {
  font-size: 11px;
  color: #aaa;
}

@media (max-width: 480px) {
  .project-card {
    padding: 14px;

    &__metrics {
      gap: 4px;
    }
  }

  .metric {
    &__value {
      font-size: 12px;
    }

    &__label {
      font-size: 9px;
    }
  }
}
</style>
